import { useQueryClient } from '@tanstack/react-query';
import Router from 'next/router';
import { type MouseEvent, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Boards, type GetBoardResponse } from '@air/api';
import { type Board } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getPrivateBoardKey } from '~/components/v2/hooks/queries/usePrivateBoard';
import { getCanvasQueryParams } from '~/constants/canvasQueryParams';
import { createBoardRoute } from '~/constants/routes';
import { setCentralizedBoardAction, setCentralizedBoardIdAndTitleAction } from '~/store/centralizedBoard/actions';

export const usePrivateBoardLinkClick = ({ board }: { board: Board }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const onBoardLinkClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      const cachedBoard = queryClient.getQueryData<GetBoardResponse>(getPrivateBoardKey({ boardId: board.id }));
      const isModifierPressed = event.metaKey || event.ctrlKey || event.shiftKey;
      const currentBoardId = Array.isArray(Router.query.boardId) ? Router.query.boardId[0] : Router.query.boardId;

      if (currentBoardId === board.id) {
        event.preventDefault();
        return;
      }

      if (!isModifierPressed) {
        // Prevent the Link from navigating to the bare pathname (which would strip cnId/cnFs).
        // We handle navigation ourselves to preserve canvas params without leaking other board state.
        event.preventDefault();

        if (cachedBoard) {
          dispatch(setCentralizedBoardAction({ board: cachedBoard }));
        } else {
          dispatch(setCentralizedBoardIdAndTitleAction({ board }));
        }

        await Router.push({ pathname: createBoardRoute(board.id, board.title), query: getCanvasQueryParams() });
      }

      try {
        await Boards.viewBoard({ boardId: board.id });
      } catch (error) {
        reportErrorToBugsnag({
          error,
          context: 'Failed to trigger a view for a board',
          metadata: { data: { boardId: board.id } },
        });
      }
    },
    [board, dispatch, queryClient],
  );

  return {
    onBoardLinkClick,
  };
};
